import React, { useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/HelpPage.css';

interface HelpPageProps {
  onClose: () => void;
}

const HelpPage: React.FC<HelpPageProps> = ({ onClose }) => {
  const { t } = useTranslation();
  
  useEffect(() => {
    // スパム対策: メールアドレスを動的に構築
    // innerHTML は XSS リスクがあるため createElement + textContent を使用
    const emailElement = document.querySelector('.help-email-contact');
    if (emailElement) {
      const user = emailElement.querySelector('[data-user]')?.getAttribute('data-user') || '';
      const domain = emailElement.querySelector('[data-domain]')?.getAttribute('data-domain') || '';
      if (user && domain) {
        const email = user + '@' + domain;
        const label = document.createTextNode('Email: ');
        const link = document.createElement('a');
        link.href = `mailto:${email}`;
        link.style.color = 'inherit';
        link.textContent = email;
        emailElement.replaceChildren(label, link);
      }
    }
  }, []);
  return (
    <div className="help-overlay" onClick={onClose}>
      <div className="help-content" onClick={(e) => e.stopPropagation()}>
        <div className="help-header">
          <h2>📚 {t('help.title')}</h2>
          <button className="help-close" onClick={onClose}>×</button>
        </div>
        
        <div className="help-body">
          <section className="help-section">
            <h3>🚀 {t('help.gettingStarted')}</h3>
            <p>{t('help.introduction')}</p>
          </section>

          <section className="help-section">
            <h3>✨ {t('help.howToUse')}</h3>
            <div className="help-item">
              <h4>1. {t('help.addHabit')}</h4>
              <p>{t('help.addHabitDesc')}</p>
              <ul>
                <li>{t('help.addHabitSteps.name')}</li>
                <li>{t('help.addHabitSteps.description')}</li>
                <li>{t('help.addHabitSteps.iconColor')}</li>
                <li>{t('help.addHabitSteps.days')}</li>
                <li>{t('help.addHabitSteps.goalType')}</li>
              </ul>
            </div>

            <div className="help-item">
              <h4>2. {t('help.recordEntry')}</h4>
              <p>{t('help.recordEntryDesc')}</p>
              <ul>
                <li>⭕ <strong>{t('record.completed')}</strong>: {t('help.recordStatus.completed').split(':')[1]}</li>
                <li>→ <strong>{t('record.skipped')}</strong>: {t('help.recordStatus.skipped').split(':')[1]}</li>
                <li>✗ <strong>{t('record.failed')}</strong>: {t('help.recordStatus.failed').split(':')[1]}</li>
                <li>　 <strong>{t('record.pending')}</strong>: {t('help.recordStatus.pending').split(':')[1]}</li>
              </ul>
            </div>

            <div className="help-item">
              <h4>3. {t('help.monthlyView')}</h4>
              <p>{t('help.monthlyViewDesc')}</p>
              <ul>
                <li>{t('help.monthlyViewFeatures.rate')}</li>
                <li>{t('help.monthlyViewFeatures.stats')}</li>
                <li>{t('help.monthlyViewFeatures.daily')}</li>
              </ul>
            </div>
          </section>

          <section className="help-section">
            <h3>📊 {t('help.features')}</h3>
            <div className="help-item">
              <h4>{t('help.streak')}</h4>
              <p>{t('help.streakDesc')}</p>
            </div>

            <div className="help-item">
              <h4>{t('help.weeklyGoal')}</h4>
              <p>{t('help.weeklyGoalDesc')}</p>
            </div>

            <div className="help-item">
              <h4>🏆 {t('help.badgeSystem')}</h4>
              <p>{t('help.badgeSystemDesc')}</p>
            </div>

            <div className="help-item">
              <h4>{t('help.offline')}</h4>
              <p>{t('help.offlineDesc')}</p>
            </div>
          </section>

          <section className="help-section">
            <h3>💡 {t('help.faq')}</h3>
            <div className="help-item">
              <h4>{t('help.faqEditPast')}</h4>
              <p>{t('help.faqEditPastAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>{t('help.faqReorder')}</h4>
              <p>{t('help.faqReorderAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>{t('help.faqDataLoss')}</h4>
              <p>{t('help.faqDataLossAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>{t('help.faqMobile')}</h4>
              <p>{t('help.faqMobileAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>🔔 {t('help.faqNotification')}</h4>
              <p>{t('help.faqNotificationAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>📅 {t('help.faqWeeklyGoal')}</h4>
              <p>{t('help.faqWeeklyGoalAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>🏆 {t('help.faqBadges')}</h4>
              <p>{t('help.faqBadgesAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>📱 {t('help.faqPWA')}</h4>
              <p>{t('help.faqPWAAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>🔒 {t('help.faqPrivacy')}</h4>
              <p>{t('help.faqPrivacyAnswer')}</p>
            </div>

            <div className="help-item">
              <h4>💰 {t('help.faqFree')}</h4>
              <p>
                {t('help.faqFreeAnswer')}
                <a href={t('help.faqFreeCompanyUrl')} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                  {t('help.faqFreeCompany')}
                </a>
                {t('help.faqFreeAnswerSuffix')}
              </p>
            </div>
          </section>

          <section className="help-section">
            <h3>🎯 {t('help.tips')}</h3>
            <ul>
              <li><strong>{t('help.tipsItems.small').split(':')[0]}</strong>: {t('help.tipsItems.small').split(':')[1]}</li>
              <li><strong>{t('help.tipsItems.visualize').split(':')[0]}</strong>: {t('help.tipsItems.visualize').split(':')[1]}</li>
              <li><strong>{t('help.tipsItems.noPerfect').split(':')[0]}</strong>: {t('help.tipsItems.noPerfect').split(':')[1]}</li>
              <li><strong>{t('help.tipsItems.routine').split(':')[0]}</strong>: {t('help.tipsItems.routine').split(':')[1]}</li>
              <li><strong>{t('help.tipsItems.review').split(':')[0]}</strong>: {t('help.tipsItems.review').split(':')[1]}</li>
            </ul>
          </section>

          <section className="help-section">
            <h3>📞 {t('help.support')}</h3>
            <p>{t('help.supportDesc')}</p>
            <p className="help-email-contact">
              <span data-user="habit.tracker.app2025"></span>
              <span data-domain="gmail.com"></span>
            </p>
            <p>{t('help.feedback')}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;